<template>
  <v-bottom-navigation
    v-if="isMobile"
    v-model="activeTab"
    class="mobile-bottom-nav"
    :elevation="8"
    grow
    bg-color="surface"
  >
    <v-btn
      v-for="item in navigationItems"
      :key="item.to"
      :value="item.to"
      :to="item.to"
      class="nav-item"
    >
      <v-icon :icon="item.icon" />
      <span class="nav-label">{{ $t(item.label) }}</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const { t } = useI18n()
const { smAndDown } = useDisplay()
const authStore = useAuthStore()

const isMobile = computed(() => smAndDown.value)
const activeTab = ref('/')

// Navigation items
const navigationItems = computed(() => [
  {
    to: '/',
    icon: 'mdi-home',
    label: 'navbar.home'
  },
  {
    to: '/courses',
    icon: 'mdi-book-open-variant',
    label: 'navbar.courses'
  },
  {
    to: '/instructors',
    icon: 'mdi-account-group',
    label: 'navbar.instructorsShort'
  },
  {
    // Динамический маршрут для тестов
    to: (() => {
      if (!authStore.isAuthenticated) return '/tests'
      
      const role = authStore.user?.role
      switch (role) {
        case 'STUDENT':
          return '/student/tests'
        case 'ADMIN':
        case 'ADMIN_LIMITED':
          return '/admin/test-system'
        case 'THEORY_TEACHER':
          return '/teacher/tests'
        case 'LIMITED_STUDENT':
          return '/student?restricted=true'
        case 'INSTRUCTOR':
          return '/tests' // Инструктор үшін публикалық тест беті
        default:
          return '/tests'
      }
    })(),
    icon: 'mdi-clipboard-text',
    label: 'navbar.tests'
  },
  {
    to: '/contact',
    icon: 'mdi-phone',
    label: 'navbar.contact'
  }
])

// Update active tab when route changes
watch(() => route.path, (newPath) => {
  // Find exact match or closest parent path
  const matchedItem = navigationItems.value.find(item => {
    if (item.to === '/') {
      return newPath === '/'
    }
    return newPath.startsWith(item.to)
  })
  
  if (matchedItem) {
    activeTab.value = matchedItem.to
  }
}, { immediate: true })
</script>

<style lang="scss" scoped>
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  height: calc(64px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
  
  :deep(.v-btn) {
    min-width: 0;
    flex-direction: column;
    height: 100%;
    padding-inline: 2px;
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .v-btn__content {
      flex-direction: column;
      gap: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      width: 100%;
    }
    
    .v-icon {
      font-size: 24px;
      margin-bottom: 2px;
    }
  }
  
  .nav-label {
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1.1;
    text-transform: none;
    letter-spacing: 0;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
    width: 100%;
  }
  
  // Keep consistent text size for all buttons
  :deep(.v-btn) {
    .nav-label {
      font-size: 0.7rem !important;
    }
  }
}

@media (max-width: 380px) {
  .mobile-bottom-nav {
    :deep(.v-btn) {
      .v-btn__content {
        gap: 2px;
      }

      .v-icon {
        font-size: 22px;
        margin-bottom: 0;
      }
    }

    .nav-label {
      font-size: 0.62rem !important;
      line-height: 1 !important;
    }
  }
}

// Add padding to main content to prevent overlap
:deep(.v-main) {
  @media (max-width: 959px) {
    padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px)) !important;
  }
}
</style>
